import { useClient, useItemContext, useQuery } from '@weenat/client'
import { isVirtualWeatherStation } from '@weenat/client/dist/core/devices'
import { AllowedMetricsInHistory } from '@weenat/client/dist/core/history/history'
import { BillingStatus } from '@weenat/client/dist/enums'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import DumbMetricSummaryCondensed from 'app/src/dashboard/components/common/summary/dailyWeather/DumbMetricSummaryCondensed'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import { useToggleFeature } from 'app/state'
import { isNil } from 'lodash-es'
import Card from '../Card'
import DelimitedFlex from '../DelimitedFlex'
import Icons from '../Icons'
import Text from '../Text'
import MetricIcons from '../icons/MetricIcons'
import LoadingCircle from '../loaders/LoadingCircle'
import WeedgetCard from './WeedgetCard'

interface LatestValueCardProps {
  metric: AllowedMetricsInHistory
}

const LegacyLatestValueCard: FC<LatestValueCardProps> = ({ metric }) => {
  const { t } = useIntl()
  const org = useOrgContext()
  const MetricIcon = MetricIcons[metric]
  const isExpertOrg = org.billingStatus === BillingStatus.expert

  const { id } = useItemContext()
  const client = useClient()
  const deviceRequest = useQuery(client.devices.get(id), { enabled: !isNil(id) })
  const device = deviceRequest.data

  const deviceSummaryRequest = useQuery(
    client.measurements.devices.summaries.getOne({ deviceId: id })
  )
  const summary = deviceSummaryRequest.data?.data

  const { isRestricted } = useIsAppAccessRestricted({
    isVWS: !isNil(device) && isVirtualWeatherStation(device)
  })

  return metric === 'LW_D' ? null : (
    <WeedgetCard $minHeight={'unset'}>
      <DelimitedFlex $alignItems='center' $justifyContent='space-between' $p='md' $gap='md'>
        <Flex $alignItems='center' $gap='md' $overflow='hidden' $pr='md'>
          <MetricIcon
            $size='lg'
            $backgroundColor={`metrics.${metric}.500`}
            $p='sm'
            $color='grayscale.white'
          />
          <Text $flexed $fontWeight='bold' $ellipsis>
            {t(`metrics.${metric}`, { capitalize: true })}
          </Text>
        </Flex>
      </DelimitedFlex>
      {!isRestricted ? (
        <Flex $flexDirection='column' $flex={1}>
          <Flex $gap='lg' $px='md' $pt='md' $pb='sm' $flex={1}>
            {isNil(device) || isNil(summary) ? (
              <Flex $p='md' $flex={1} $justifyContent='center' $alignItems='center'>
                <LoadingCircle size='xl' color='grayscale.black' />
              </Flex>
            ) : (
              <DumbMetricSummaryCondensed
                metric={metric}
                resource={device}
                summary={summary}
                isExpertOrg={isExpertOrg}
              />
            )}
          </Flex>
        </Flex>
      ) : (
        <Flex $flex={1} $p='md'>
          <Flex
            $flex={1}
            $backgroundColor={'grayscale.black'}
            $alignItems='center'
            $justifyContent='center'
            $borderRadius={'md'}
            $flexDirection='column'
            $gap='sm'
            $p='md'
          >
            <Icons.Lock $size='lg' $color={'grayscale.white'} />
            <Text $color='grayscale.white' $fontSize='sm' $textAlign='center'>
              {t('ecommerce.plans.restrictions.carousel_value_downgrade_title')}
            </Text>
          </Flex>
        </Flex>
      )}
    </WeedgetCard>
  )
}

const NewLatestValueCard: FC<LatestValueCardProps> = ({ metric }) => {
  const { t } = useIntl()
  const org = useOrgContext()
  const MetricIcon = MetricIcons[metric]
  const isExpertOrg = org.billingStatus === BillingStatus.expert

  const { id } = useItemContext()
  const client = useClient()
  const deviceRequest = useQuery(client.devices.get(id), { enabled: !isNil(id) })
  const device = deviceRequest.data

  const deviceSummaryRequest = useQuery(
    client.measurements.devices.summaries.getOne({ deviceId: id })
  )
  const summary = deviceSummaryRequest.data?.data

  const { isRestricted } = useIsAppAccessRestricted({
    isVWS: !isNil(device) && isVirtualWeatherStation(device)
  })

  return metric === 'LW_D' ? null : (
    <Card $p={0}>
      <Flex $p={'md'} $alignItems='center' $gap='md'>
        <MetricIcon $size='lg' $color={`metrics.${metric}.500`} />
        <Text $flexed $fontWeight='medium' $ellipsis>
          {t(`metrics.${metric}${metric === 'FF' ? '_abbr' : ''}`, { capitalize: true })}
        </Text>
      </Flex>
      {!isRestricted ? (
        <>
          {isNil(device) || isNil(summary) ? (
            <Flex $p='md' $flex={1} $justifyContent='center' $alignItems='center'>
              <LoadingCircle size='xl' color='grayscale.black' />
            </Flex>
          ) : (
            <DumbMetricSummaryCondensed
              metric={metric}
              resource={device}
              summary={summary}
              isExpertOrg={isExpertOrg}
            />
          )}
        </>
      ) : (
        <Flex $flex={1} $p='md'>
          <Flex
            $flex={1}
            $backgroundColor={'grayscale.black'}
            $alignItems='center'
            $justifyContent='center'
            $borderRadius={'md'}
            $flexDirection='column'
            $gap='sm'
            $p='md'
          >
            <Icons.Lock $size='lg' $color={'grayscale.white'} />
            <Text $color='grayscale.white' $fontSize='sm' $textAlign='center'>
              {t('ecommerce.plans.restrictions.carousel_value_downgrade_title')}
            </Text>
          </Flex>
        </Flex>
      )}
    </Card>
  )
}

const LatestValueCard: FC<LatestValueCardProps> = ({ metric }) => {
  const [{ sdh }] = useToggleFeature()

  return sdh ? <NewLatestValueCard metric={metric} /> : <LegacyLatestValueCard metric={metric} />
}

export default LatestValueCard
